/*
 * Today's Pool Forecast — Pool Busters
 * Local weather → pool-care guidance with up to three catalogue products.
 *
 *  - Vanilla JS, no libraries. Async, non-blocking. Multiple instances safe.
 *  - No API key of any kind in the browser. Open-Meteo is keyless; an optional
 *    proxy endpoint is supported for a paid provider but is not required.
 *  - Editable thresholds + per-state products come from the section (data-attrs
 *    + inline JSON), so staff tune everything in the Theme Editor.
 *  - Overlapping conditions merge (e.g. "Sunny & Windy"); products de-duped and
 *    capped. Priority decides the primary message + featured product.
 *  - Polished loading / live / stale / unavailable / no-JS states. Never shows
 *    raw errors, empty cards, or sold-out products.
 *
 *  LOCATION (see resolveLocation). Three sources, tried in order, every result
 *  put through the SAME validator before a customer can see it:
 *    1. A location already saved in this browser (localStorage, 7 days).
 *    2. Browser geolocation — ONLY when the Permissions API reports the
 *       permission is already 'granted'. The widget never triggers a prompt.
 *    3. Approximate city from a keyless IP lookup (GeoJS).
 *  validateLocation rejects anything outside SERVICE_RADIUS_KM of the store,
 *  anything outside Canada/US, and any unusable city name, so a wrong IP-hub
 *  city can never reach the page. On rejection or failure the widget shows
 *  Fonthill, Ontario — never a random city and never an error.
 *
 *  - No cookies and no tracking. localStorage holds one entry: the resolved
 *    city name and coordinates, plus a short-lived weather reading. First-party
 *    only, never transmitted anywhere.
 */
(function () {
  'use strict';

  if (window.PoolForecast && window.PoolForecast.__loaded) { window.PoolForecast.scan(); return; }

  /* ----------------------------------------------------------------------- *
   * Condition priority (index 0 = highest). Drives primary message + featured.
   * ----------------------------------------------------------------------- */
  var PRIORITY = ['storm', 'heavy_rain', 'strong_wind', 'rain', 'wind', 'sun_uv', 'hot', 'mild'];

  // Short descriptors used to build a human label like "Sunny & Windy".
  var STATE_LABEL = {
    storm: 'Stormy', heavy_rain: 'Heavy Rain', strong_wind: 'Very Windy',
    rain: 'Rainy', wind: 'Windy', sun_uv: 'Sunny', hot: 'Warm', mild: 'Mild'
  };

  /* ----------------------------------------------------------------------- *
   * WMO weather-code helpers (Open-Meteo direct + proxy both normalise to WMO).
   *  thunder: 95,96,99 · rain/drizzle/showers/snow: 51-86 · clear: 0,1
   * ----------------------------------------------------------------------- */
  var THUNDER = [95, 96, 99];
  var RAIN_CODES = [51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 71, 73, 75, 77, 80, 81, 82, 85, 86];
  var CLEARISH = [0, 1];
  function inList(list, c) { return list.indexOf(c) !== -1; }
  var CODE_LABELS = { 0:'Clear', 1:'Mainly clear', 2:'Partly cloudy', 3:'Cloudy', 45:'Fog', 48:'Fog', 51:'Light drizzle', 53:'Drizzle', 55:'Heavy drizzle', 56:'Freezing drizzle', 57:'Freezing drizzle', 61:'Light rain', 63:'Rain', 65:'Heavy rain', 66:'Freezing rain', 67:'Freezing rain', 71:'Light snow', 73:'Snow', 75:'Heavy snow', 77:'Snow grains', 80:'Light showers', 81:'Showers', 82:'Heavy showers', 85:'Snow showers', 86:'Snow showers', 95:'Thunderstorm', 96:'Thunderstorm', 99:'Thunderstorm' };
  function wmoLabel(c) { return CODE_LABELS[c] || 'Current conditions'; }

  /* ----------------------------------------------------------------------- *
   * THE CLASSIFIER — returns the set of ACTIVE states given normalised weather
   * and editable thresholds. Multiple may be active at once.
   * ----------------------------------------------------------------------- */
  function activeStates(w, t) {
    var code = numOr(w.code, -1);
    var maxTemp = numOr(w.maxTempC, w.tempC);
    var precip = numOr(w.precipMm, 0);
    var chance = numOr(w.chanceOfRain, 0);
    var maxWind = numOr(w.maxWindKph, w.windKph);
    var gust = numOr(w.gustKph, 0);
    var uv = Math.max(numOr(w.uv, 0), numOr(w.dayUv, 0));
    var hasAlert = !!w.alert;

    var isThunder = inList(THUNDER, code);
    var isRainCode = inList(RAIN_CODES, code);
    var isClear = inList(CLEARISH, code);

    var s = {};
    // Storm / severe: thunder code, severe gusts, or an official alert.
    s.storm = isThunder || gust >= t.strong_gust || hasAlert;
    s.heavy_rain = precip >= t.heavy_rain_precip;
    s.strong_wind = gust >= t.strong_gust;
    s.rain = isRainCode || precip >= t.rain_precip || chance >= t.rain_chance;
    s.wind = maxWind >= t.wind_max || gust >= t.wind_gust;
    s.sun_uv = isClear && uv >= t.uv_min;
    s.hot = maxTemp >= t.hot_temp;

    var active = [];
    for (var i = 0; i < PRIORITY.length; i++) {
      var key = PRIORITY[i];
      if (key === 'mild') continue;
      if (s[key]) active.push(key);
    }
    if (active.length === 0) active.push('mild');
    return active;
  }

  function numOr(v, f) { return (typeof v === 'number' && !isNaN(v)) ? v : f; }

  /* ----------------------------------------------------------------------- *
   * Build the recommendation set: featured + up to (maxRecs-1) supporting,
   * merged across all ACTIVE + ENABLED states, de-duplicated, sold-out/blank
   * products dropped.
   * ----------------------------------------------------------------------- */
  function buildRecommendations(active, states, maxRecs) {
    // Primary = highest-priority active state that is ENABLED.
    var primary = null;
    for (var i = 0; i < active.length; i++) {
      if (states[active[i]] && states[active[i]].enabled) { primary = active[i]; break; }
    }
    if (!primary) primary = active[0];

    var picks = [];
    var seen = {};
    function add(p) {
      if (!p || !p.title || p.available === false || !p.url) return;
      var id = p.url;
      if (seen[id]) return;
      seen[id] = true;
      picks.push(p);
    }

    // Featured from the primary state first.
    var prim = states[primary] || {};
    if (prim.enabled) { add(prim.featured); }

    // Then supporting from primary, then other active states (priority order).
    if (prim.enabled && prim.supporting) prim.supporting.forEach(add);
    for (var j = 0; j < active.length; j++) {
      var st = states[active[j]];
      if (!st || !st.enabled || active[j] === primary) continue;
      add(st.featured);
      if (st.supporting) st.supporting.forEach(add);
    }

    if (picks.length > maxRecs) picks = picks.slice(0, maxRecs);
    return { primary: primary, featured: picks[0] || null, supporting: picks.slice(1), all: picks };
  }

  /* ----------------------------------------------------------------------- *
   * Human condition label from active states, e.g. "Sunny & Windy".
   * ----------------------------------------------------------------------- */
  function conditionLabel(active, conditionText) {
    if (active.length === 1 && active[0] === 'mild') return 'Mild';
    var parts = [];
    for (var i = 0; i < active.length && parts.length < 2; i++) {
      var lbl = STATE_LABEL[active[i]];
      if (lbl && parts.indexOf(lbl) === -1) parts.push(lbl);
    }
    if (!parts.length) return conditionText || 'Current conditions';
    return parts.join(' & ');
  }

  /* ----------------------------------------------------------------------- *
   * Inline SVG icons (line style, no emoji). Chosen from the primary state.
   * ----------------------------------------------------------------------- */
  var ICONS = {
    sun: '<svg viewBox="0 0 48 48" fill="none" aria-hidden="true"><circle cx="24" cy="24" r="9" stroke="currentColor" stroke-width="2"/><g stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M24 5v5"/><path d="M24 38v5"/><path d="M5 24h5"/><path d="M38 24h5"/><path d="M10.6 10.6l3.5 3.5"/><path d="M33.9 33.9l3.5 3.5"/><path d="M37.4 10.6l-3.5 3.5"/><path d="M14.1 33.9l-3.5 3.5"/></g></svg>',
    cloud: '<svg viewBox="0 0 48 48" fill="none" aria-hidden="true"><path d="M16 36h18a7 7 0 0 0 .5-13.98A9 9 0 0 0 17 20.2 7.5 7.5 0 0 0 16 36Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/></svg>',
    rain: '<svg viewBox="0 0 48 48" fill="none" aria-hidden="true"><path d="M16 30h18a7 7 0 0 0 .5-13.98A9 9 0 0 0 17 14.2 7.5 7.5 0 0 0 16 30Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><g stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 36l-2 5"/><path d="M26 36l-2 5"/><path d="M34 36l-2 5"/></g></svg>',
    heavyrain: '<svg viewBox="0 0 48 48" fill="none" aria-hidden="true"><path d="M16 28h18a7 7 0 0 0 .5-13.98A9 9 0 0 0 17 12.2 7.5 7.5 0 0 0 16 28Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><g stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M15 33l-3 7"/><path d="M22 33l-3 7"/><path d="M29 33l-3 7"/><path d="M36 33l-3 7"/></g></svg>',
    storm: '<svg viewBox="0 0 48 48" fill="none" aria-hidden="true"><path d="M16 28h18a7 7 0 0 0 .5-13.98A9 9 0 0 0 17 12.2 7.5 7.5 0 0 0 16 28Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><path d="M25 30l-5 7h5l-2 6 7-9h-5l3-4Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/></svg>',
    wind: '<svg viewBox="0 0 48 48" fill="none" aria-hidden="true"><g stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 19h20a5 5 0 1 0-5-5"/><path d="M6 27h28a5 5 0 1 1-5 5"/><path d="M6 35h14a4 4 0 1 1-4 4"/></g></svg>'
  };
  var STATE_ICON = { storm: 'storm', heavy_rain: 'heavyrain', strong_wind: 'wind', rain: 'rain', wind: 'wind', sun_uv: 'sun', hot: 'sun', mild: 'cloud' };

  /* ----------------------------------------------------------------------- *
   * Mock weather for the Theme-Editor / dev test mode.
   * ----------------------------------------------------------------------- */
  var MOCKS = {
    sunny:      { code:0,  tempC:29, maxTempC:31, minTempC:18, maxWindKph:12, windKph:9, gustKph:16, precipMm:0, chanceOfRain:5, uv:8, condition:'Clear', isDay:true },
    rain:       { code:63, tempC:18, maxTempC:20, minTempC:14, maxWindKph:15, windKph:11, gustKph:20, precipMm:4, chanceOfRain:80, uv:2, condition:'Rain', isDay:true },
    heavy_rain: { code:65, tempC:17, maxTempC:19, minTempC:13, maxWindKph:22, windKph:16, gustKph:30, precipMm:14, chanceOfRain:95, uv:1, condition:'Heavy rain', isDay:true },
    wind:       { code:2,  tempC:22, maxTempC:24, minTempC:15, maxWindKph:28, windKph:20, gustKph:40, precipMm:0, chanceOfRain:10, uv:4, condition:'Partly cloudy', isDay:true },
    strong_wind:{ code:2,  tempC:20, maxTempC:22, minTempC:14, maxWindKph:45, windKph:35, gustKph:65, precipMm:0, chanceOfRain:10, uv:3, condition:'Partly cloudy', isDay:true },
    hot:        { code:0,  tempC:30, maxTempC:32, minTempC:21, maxWindKph:10, windKph:7, gustKph:14, precipMm:0, chanceOfRain:0, uv:6, condition:'Clear', isDay:true },
    storm:      { code:95, tempC:24, maxTempC:27, minTempC:18, maxWindKph:30, windKph:22, gustKph:45, precipMm:8, chanceOfRain:85, uv:3, condition:'Thunderstorm', isDay:true, alert:true },
    mild:       { code:3,  tempC:21, maxTempC:23, minTempC:15, maxWindKph:12, windKph:9, gustKph:16, precipMm:0, chanceOfRain:15, uv:3, condition:'Cloudy', isDay:true },
    overlap:    { code:0,  tempC:30, maxTempC:31, minTempC:20, maxWindKph:30, windKph:24, gustKph:42, precipMm:0, chanceOfRain:5, uv:8, condition:'Clear', isDay:true }
  };

  /* ------------------------------- formatting ------------------------------ */
  function toDisplayTemp(c, unit) { if (typeof c !== 'number' || isNaN(c)) return null; return unit === 'F' ? Math.round(c * 9 / 5 + 32) : Math.round(c); }
  function unitSym(unit) { return unit === 'F' ? '°F' : '°C'; }
  function fmtUpdated(s) {
    // Local string "2026-07-24 12:15" or ISO "2026-07-24T12:15" → "12:15 PM"
    if (!s) return '';
    var m = /(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(s);
    if (!m) return '';
    var h = parseInt(m[4], 10), min = m[5], ap = h >= 12 ? 'PM' : 'AM';
    h = h % 12; if (h === 0) h = 12;
    return h + ':' + min + ' ' + ap;
  }

  /* --------------------------------- DOM ----------------------------------- */
  function $(r, s) { return r.querySelector(s); }
  function setText(el, t) { if (el) el.textContent = t; }
  function show(el, on) { if (el) el.hidden = !on; }

  /* ------------------------------- fetch ----------------------------------- *
   * Two modes:
   *   1. Proxy mode  — endpoint set → call the secure proxy (WeatherAPI etc.).
   *   2. Keyless mode — endpoint blank → the widget fetches directly:
   *        a) resolve the visitor's location (resolveLocation, below);
   *        b) fetch weather from Open-Meteo (keyless) and normalise it.
   *      No API key exists, so nothing needs hiding and no proxy is required.
   * ------------------------------------------------------------------------- */

  /* The store. Every fallback lands here, and it is the centre of the radius
     check. Fonthill / Pelham, Niagara Region — Open-Meteo covers it directly. */
  var STORE = { lat: 43.0501, lon: -79.2496, label: 'Fonthill, Ontario' };

  /* A detected location further than this from the store is not believed. IP
     databases resolve a consumer address to the carrier's registered hub, which
     is routinely a different city and occasionally a different province; 150 km
     keeps every real service area (all of Niagara, Hamilton, the GTA, Buffalo)
     and discards the hub answers that caused the random-city reports. */
  var SERVICE_RADIUS_KM = 150;
  var ALLOWED_COUNTRIES = { CA: 1, US: 1 };

  var LOC_KEY = 'pf:loc:v3';
  var LOC_TTL_MIN = 7 * 24 * 60;          // a week — a visitor's city rarely moves
  var WX_PREFIX = 'pf:wx:v3:';

  /* Municipalities inside the radius, used ONLY to put a name to browser
     geolocation coordinates (which arrive without one). Keeps the label a real
     local place instead of a reverse-geocoding round trip. */
  var PLACES = [
    [43.0501, -79.2496, 'Fonthill', 'Ontario'], [42.9925, -79.2483, 'Welland', 'Ontario'],
    [43.0333, -79.3667, 'Fenwick', 'Ontario'], [42.9167, -79.4000, 'Wainfleet', 'Ontario'],
    [42.8859, -79.2506, 'Port Colborne', 'Ontario'], [43.1242, -79.1989, 'Thorold', 'Ontario'],
    [43.1594, -79.2469, 'St. Catharines', 'Ontario'], [43.1060, -79.0747, 'Niagara Falls', 'Ontario'],
    [43.2553, -79.0717, 'Niagara-on-the-Lake', 'Ontario'], [42.9049, -78.9377, 'Fort Erie', 'Ontario'],
    [42.8639, -79.0625, 'Crystal Beach', 'Ontario'], [42.8917, -79.0500, 'Ridgeway', 'Ontario'],
    [43.1667, -79.4833, 'Beamsville', 'Ontario'], [43.1994, -79.5603, 'Grimsby', 'Ontario'],
    [43.1597, -79.4011, 'Vineland', 'Ontario'], [43.1500, -79.3667, 'Jordan', 'Ontario'],
    [43.0958, -79.5500, 'Smithville', 'Ontario'], [42.9042, -79.6167, 'Dunnville', 'Ontario'],
    [43.0667, -79.9500, 'Caledonia', 'Ontario'], [43.2557, -79.8711, 'Hamilton', 'Ontario'],
    [43.2170, -79.7610, 'Stoney Creek', 'Ontario'], [43.2186, -79.9869, 'Ancaster', 'Ontario'],
    [43.2667, -79.9500, 'Dundas', 'Ontario'], [43.3255, -79.7990, 'Burlington', 'Ontario'],
    [43.3333, -79.9000, 'Waterdown', 'Ontario'], [43.4675, -79.6877, 'Oakville', 'Ontario'],
    [43.5183, -79.8774, 'Milton', 'Ontario'], [43.5890, -79.6441, 'Mississauga', 'Ontario'],
    [43.6532, -79.3832, 'Toronto', 'Ontario'], [43.7315, -79.7624, 'Brampton', 'Ontario'],
    [43.1394, -80.2644, 'Brantford', 'Ontario'], [43.3616, -80.3144, 'Cambridge', 'Ontario'],
    [43.4516, -80.4925, 'Kitchener', 'Ontario'], [43.5448, -80.2482, 'Guelph', 'Ontario'],
    [42.8373, -80.3036, 'Simcoe', 'Ontario'], [42.7861, -80.1989, 'Port Dover', 'Ontario'],
    [42.8864, -78.8784, 'Buffalo', 'New York'], [43.0962, -79.0377, 'Niagara Falls', 'New York'],
    [43.1737, -79.0353, 'Lewiston', 'New York'], [42.9784, -78.7998, 'Amherst', 'New York'],
    [43.0203, -78.8803, 'Tonawanda', 'New York'], [43.1706, -78.6903, 'Lockport', 'New York']
  ];

  function haversineKm(aLat, aLon, bLat, bLon) {
    var R = 6371, toRad = Math.PI / 180;
    var dLat = (bLat - aLat) * toRad, dLon = (bLon - aLon) * toRad;
    var s = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(aLat * toRad) * Math.cos(bLat * toRad) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
  }

  /* Nearest municipality to a coordinate, but only if it is close enough to be
     an honest description of that coordinate. Used to name a GPS fix, and to
     name coordinates whose provider sent an unusable city. Returns null when
     the coordinate is not near any place we know, so a point in a rural gap is
     never labelled with a town 80 km away. */
  var NEAREST_PLACE_MAX_KM = 35;
  function nearestPlace(lat, lon, maxKm) {
    var limit = (typeof maxKm === 'number') ? maxKm : NEAREST_PLACE_MAX_KM;
    var best = null, bestD = Infinity;
    for (var i = 0; i < PLACES.length; i++) {
      var d = haversineKm(lat, lon, PLACES[i][0], PLACES[i][1]);
      if (d < bestD) { bestD = d; best = PLACES[i]; }
    }
    if (!best || bestD > limit) return null;
    return { city: best[2], region: best[3], km: bestD };
  }

  /* A city name we are willing to print. Rejects the empty string, the literal
     words a failed lookup serialises to, pure numbers, anything with markup,
     and anything implausibly long. Returns a trimmed name or null. */
  function cleanCity(v) {
    if (typeof v !== 'string') return null;
    var s = v.replace(/\s+/g, ' ').trim();
    if (!s || s.length > 64) return null;
    if (/^(undefined|null|nan|none|unknown|n\/a|-|\.)$/i.test(s)) return null;
    if (/[<>{}\[\]\\|`=;]/.test(s)) return null;     // never markup or injection
    if (!/[A-Za-zÀ-ÿ]/.test(s)) return null;          // must contain a letter
    return s;
  }

  /* THE GATE. Every candidate location — saved, GPS or IP — comes through here
     before it can reach the page. Returns a clean location or null.
     The geographic checks are the safety property and always run. A name that
     arrives as city/region (IP and GPS) is used. A candidate with no usable
     city name is NOT allowed to supply its own label — the name is re-derived
     from the coordinates instead, so the place on screen is always the place the
     weather was fetched for, whatever a stored entry happens to claim. */
  function validateLocation(raw) {
    if (!raw) return null;
    var lat = Number(raw.lat), lon = Number(raw.lon);
    if (!isFinite(lat) || !isFinite(lon)) return null;
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
    if (lat === 0 && lon === 0) return null;                       // null island
    if (raw.country && !ALLOWED_COUNTRIES[String(raw.country).toUpperCase()]) return null;
    if (haversineKm(lat, lon, STORE.lat, STORE.lon) > SERVICE_RADIUS_KM) return null;

    var city = cleanCity(raw.city);
    var region = city ? cleanCity(raw.region) : null;
    if (!city) {
      var near = nearestPlace(lat, lon);
      if (!near) return null;
      city = near.city; region = near.region;
    }
    var label = region ? city + ', ' + region : city;
    if (!displayLabel(label)) return null;
    return { lat: lat, lon: lon, city: city, region: region || '', label: label };
  }

  function storeLocation() { return { lat: STORE.lat, lon: STORE.lon, label: STORE.label, source: 'store' }; }

  function fetchJson(url, ms) {
    var controller = ('AbortController' in window) ? new AbortController() : null;
    var timer = setTimeout(function () { if (controller) controller.abort(); }, ms || 6000);
    return fetch(url, { mode: 'cors', credentials: 'omit', signal: controller ? controller.signal : undefined })
      .then(function (r) { clearTimeout(timer); if (!r.ok) throw new Error('bad'); return r.json(); });
  }

  function getData(cfg) {
    // Proxy mode.
    if (cfg.endpoint) {
      var pk = 'pf2:' + cfg.endpoint;
      var pc = readCache(pk, cfg.cacheMin);
      if (pc) return Promise.resolve(pc);
      return fetchJson(cfg.endpoint, 8000)
        .then(function (d) { if (!d || !d.current) throw new Error('shape'); writeCache(pk, d); return d; })
        .catch(function () { return null; });
    }
    // Keyless direct mode. Location first, then weather for THAT location, and
    // the weather cache is keyed by those coordinates so a cached reading can
    // never be paired with a different city than the one on screen.
    return resolveLocation(cfg).then(function (loc) {
      var wk = WX_PREFIX + loc.lat.toFixed(2) + ',' + loc.lon.toFixed(2);
      var cached = readCache(wk, cfg.cacheMin);
      if (cached) { cached.location = loc.label; return cached; }
      return fetchOpenMeteo(loc)
        .then(function (d) { if (d) writeCache(wk, d); return d; })
        .catch(function () { return null; });
    }).catch(function () { return null; });
  }

  /* ---------------------------------------------------------------- location *
   * Never rejects. Always resolves to a validated location, falling back to the
   * store. Order: saved → already-granted browser geolocation → IP → store.
   * ------------------------------------------------------------------------- */
  function resolveLocation(cfg) {
    var saved = readSavedLocation();
    if (saved) return Promise.resolve(saved);

    return getGrantedPosition()
      .then(function (gps) {
        if (gps) return gps;
        return getIpLocation();
      })
      .then(function (loc) {
        if (!loc) return storeLocation();
        writeSavedLocation(loc);
        return loc;
      })
      .catch(function () { return storeLocation(); });
  }

  /* A saved location is re-validated on the way out: the rules may have changed
     since it was written, and a stale out-of-area entry must not be trusted. */
  function readSavedLocation() {
    var raw = readCache(LOC_KEY, LOC_TTL_MIN);
    if (!raw) return null;
    if (raw.source === 'store') return storeLocation();
    var v = validateLocation({ lat: raw.lat, lon: raw.lon, city: raw.city, region: raw.region, label: raw.label, country: raw.country });
    if (!v) return null;
    return { lat: v.lat, lon: v.lon, label: v.label, city: v.city, region: v.region, country: raw.country, source: raw.source || 'saved' };
  }
  function writeSavedLocation(loc) { writeCache(LOC_KEY, loc); }

  /* Browser geolocation, but ONLY if the visitor has already granted it to this
     site. Querying the Permissions API does not prompt; getCurrentPosition
     does, so it is never called in any other permission state. Where the
     Permissions API is missing we skip browser geolocation entirely rather than
     risk an unsolicited prompt. */
  function getGrantedPosition() {
    try {
      if (!navigator.geolocation || !navigator.permissions || !navigator.permissions.query) return Promise.resolve(null);
      return navigator.permissions.query({ name: 'geolocation' })
        .then(function (status) {
          if (!status || status.state !== 'granted') return null;
          return new Promise(function (resolve) {
            var done = false;
            var finish = function (v) { if (!done) { done = true; resolve(v); } };
            setTimeout(function () { finish(null); }, 4000);
            try {
              navigator.geolocation.getCurrentPosition(
                function (pos) {
                  var c = pos && pos.coords;
                  if (!c) return finish(null);
                  /* No city name comes with a GPS fix; validateLocation names it
                     from the coordinates, which is also what bounds it. */
                  var v = validateLocation({ lat: c.latitude, lon: c.longitude });
                  finish(v ? { lat: v.lat, lon: v.lon, label: v.label, city: v.city, region: v.region, source: 'gps' } : null);
                },
                function () { finish(null); },
                { enableHighAccuracy: false, timeout: 3500, maximumAge: 30 * 60 * 1000 }
              );
            } catch (e) { finish(null); }
          });
        })
        .catch(function () { return null; });
    } catch (e) { return Promise.resolve(null); }
  }

  /* Approximate city by IP. Keyless, no prompt, and the result is only believed
     if it survives validateLocation. */
  function getIpLocation() {
    return fetchJson('https://get.geojs.io/v1/ip/geo.json', 3000)
      .then(function (g) {
        if (!g) return null;
        var v = validateLocation({ lat: g.latitude, lon: g.longitude, city: g.city, region: g.region, country: g.country_code });
        return v ? { lat: v.lat, lon: v.lon, label: v.label, city: v.city, region: v.region, country: g.country_code, source: 'ip' } : null;
      })
      .catch(function () { return null; });
  }

  function fetchOpenMeteo(loc) {
    var url = 'https://api.open-meteo.com/v1/forecast?latitude=' + loc.lat + '&longitude=' + loc.lon +
      '&current=temperature_2m,weather_code,wind_speed_10m,wind_gusts_10m,uv_index,is_day' +
      '&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_sum,precipitation_probability_max,wind_speed_10m_max,wind_gusts_10m_max,uv_index_max' +
      '&timezone=auto&wind_speed_unit=kmh&forecast_days=1';
    return fetchJson(url, 8000).then(function (d) { return normalizeOpenMeteo(d, loc); });
  }

  function normalizeOpenMeteo(d, loc) {
    if (!d || !d.current || !d.daily) throw new Error('shape');
    var c = d.current, dy = d.daily, first = function (a) { return (a && a.length) ? a[0] : null; };
    var code = numOr(c.weather_code, 1);
    var curGust = numN(c.wind_gusts_10m), dayGust = numN(first(dy.wind_gusts_10m_max));
    var gust = Math.max(curGust || 0, dayGust || 0);
    return {
      location: loc.label,
      tz: d.timezone || 'America/Toronto',
      updated: c.time || null,
      current: {
        tempC: numN(c.temperature_2m), code: code, condition: wmoLabel(code),
        windKph: numN(c.wind_speed_10m), gustKph: gust, uv: numN(c.uv_index), isDay: c.is_day === 1
      },
      today: {
        maxTempC: numN(first(dy.temperature_2m_max)), minTempC: numN(first(dy.temperature_2m_min)),
        maxWindKph: numN(first(dy.wind_speed_10m_max)), precipMm: numN(first(dy.precipitation_sum)),
        chanceOfRain: numN(first(dy.precipitation_probability_max)), uv: numN(first(dy.uv_index_max))
      },
      alerts: [], stale: false, source: 'open-meteo'
    };
  }
  function numN(v) { var n = Number(v); return (v === null || v === undefined || v === '' || isNaN(n)) ? null : n; }
  /* Storage: localStorage so a resolved location survives new tabs and return
     visits (the whole point — a per-tab cache re-detects on every new tab and
     that is what made the city appear to change). Falls back to sessionStorage,
     then to memory, so private mode and disabled storage degrade silently
     instead of throwing. */
  var memStore = {};
  function pickStorage() {
    var probe = 'pf:probe';
    var candidates = ['localStorage', 'sessionStorage'];
    for (var i = 0; i < candidates.length; i++) {
      try {
        var s = window[candidates[i]];
        if (!s) continue;
        s.setItem(probe, '1'); s.removeItem(probe);
        return s;
      } catch (e) { /* blocked — try the next one */ }
    }
    return {
      getItem: function (k) { return Object.prototype.hasOwnProperty.call(memStore, k) ? memStore[k] : null; },
      setItem: function (k, v) { memStore[k] = String(v); },
      removeItem: function (k) { delete memStore[k]; }
    };
  }
  var STORAGE = null;
  function storage() { if (!STORAGE) STORAGE = pickStorage(); return STORAGE; }

  function readCache(k, min) {
    try {
      var raw = storage().getItem(k);
      if (!raw) return null;
      var o = JSON.parse(raw);
      if (!o || typeof o._t !== 'number') return null;
      if (Date.now() - o._t > (min > 0 ? min : 45) * 60000) return null;
      return o.d;
    } catch (e) { return null; }
  }
  function writeCache(k, d) { try { storage().setItem(k, JSON.stringify({ _t: Date.now(), d: d })); } catch (e) {} }

  /* ------------------------------- config ---------------------------------- */
  function readConfig(root) {
    var d = root.dataset;
    var states = {};
    var sj = root.querySelector('[data-pf-states]');
    if (sj) { try { states = JSON.parse(sj.textContent || '{}'); } catch (e) { states = {}; } }
    return {
      endpoint: d.endpoint || '',
      unit: d.unit === 'F' ? 'F' : 'C',
      cacheMin: parseInt(d.cacheMin, 10) || 45,
      location: d.location || '',
      maxRecs: Math.max(1, Math.min(3, parseInt(d.maxRecs, 10) || 3)),
      testMode: d.testMode || 'off',
      showUpdated: d.showUpdated === 'true',
      showMeta: d.showMeta === 'true',
      thresholds: {
        rain_precip: parseFloat(d.tRainPrecip) || 2,
        rain_chance: parseFloat(d.tRainChance) || 60,
        heavy_rain_precip: parseFloat(d.tHeavyPrecip) || 10,
        wind_max: parseFloat(d.tWindMax) || 25,
        wind_gust: parseFloat(d.tWindGust) || 35,
        strong_gust: parseFloat(d.tStrongGust) || 60,
        uv_min: parseFloat(d.tUvMin) || 5,
        hot_temp: parseFloat(d.tHotTemp) || 28
      },
      states: states
    };
  }

  /* ------------------------------- render ---------------------------------- */
  function render(root, cfg, data) {
    var isStale = !!data.stale;
    var cur = data.current || {};
    var today = data.today || {};
    var w = {
      code: cur.code, tempC: cur.tempC, windKph: cur.windKph, gustKph: cur.gustKph, uv: cur.uv,
      maxTempC: today.maxTempC, minTempC: today.minTempC, maxWindKph: today.maxWindKph,
      precipMm: today.precipMm, chanceOfRain: today.chanceOfRain, dayUv: today.uv,
      alert: (data.alerts && data.alerts.length > 0)
    };

    /* Location label. The label and the reading below it come from this one
       `data` object, so what the customer reads and what the recommendation
       engine scores are always the same place. Only a label that survives
       cleanCity is written; anything else leaves the server-rendered Niagara
       default in place, so "undefined" can never appear. */
    var shownLocation = displayLabel(data.location) || displayLabel(cfg.location) || STORE.label;
    setText($(root, '[data-pf-location]'), shownLocation);

    var active = activeStates(w, cfg.thresholds);
    var recs = buildRecommendations(active, cfg.states, cfg.maxRecs);
    var primaryState = cfg.states[recs.primary] || {};

    // Icon + condition label + temperature.
    var iconEl = $(root, '[data-pf-icon]');
    if (iconEl) iconEl.innerHTML = ICONS[STATE_ICON[recs.primary]] || ICONS.sun;
    setText($(root, '[data-pf-condition]'), conditionLabel(active, cur.condition));
    var t = toDisplayTemp(cur.tempC, cfg.unit);
    setText($(root, '[data-pf-temp]'), t === null ? '' : t + unitSym(cfg.unit));

    // Meta line (high/low, chance of rain, wind).
    var metaEl = $(root, '[data-pf-meta]');
    if (metaEl && cfg.showMeta) {
      var bits = [];
      var hi = toDisplayTemp(today.maxTempC, cfg.unit), lo = toDisplayTemp(today.minTempC, cfg.unit);
      if (hi !== null) bits.push('High ' + hi + '° · Low ' + (lo === null ? '—' : lo + '°'));
      if (typeof today.chanceOfRain === 'number') bits.push(today.chanceOfRain + '% rain');
      if (typeof today.maxWindKph === 'number') bits.push('Wind to ' + Math.round(today.maxWindKph) + ' km/h');
      metaEl.textContent = bits.join('  ·  ');
      show(metaEl, bits.length > 0);
    }

    // Updated time + stale note.
    var updEl = $(root, '[data-pf-updated]');
    if (updEl && cfg.showUpdated) {
      var u = fmtUpdated(data.updated);
      updEl.textContent = (isStale ? 'Last updated ' : 'Updated ') + (u || 'recently');
      show(updEl, true);
    } else { show(updEl, false); }

    // Pool-care message (primary state's editable text).
    setText($(root, '[data-pf-message]'), primaryState.message || '');

    // Storm safety notice takes over the recommendation area.
    var isStormSafety = (recs.primary === 'storm');

    // Recommendations.
    renderRecs(root, recs, isStormSafety);

    // Attribution reflects the actual weather source.
    var attr = $(root, '[data-pf-attr]');
    if (attr) {
      if (data.source === 'weatherapi') { attr.textContent = 'Powered by WeatherAPI.com'; attr.setAttribute('href', 'https://www.weatherapi.com/'); }
      else if (data.source === 'open-meteo') { attr.textContent = 'Weather data by Open-Meteo.com'; attr.setAttribute('href', 'https://open-meteo.com/'); }
    }

    setState(root, 'ready');
    var live = $(root, '[data-pf-live]');
    if (live) live.textContent = 'Today’s pool forecast for ' + shownLocation + ' is ready.';
  }

  /* A label is printable only if every comma-separated part is a clean name.
     Guards the one string a customer reads before anything else. */
  function displayLabel(v) {
    if (typeof v !== 'string') return null;
    var parts = v.split(',');
    var out = [];
    for (var i = 0; i < parts.length; i++) {
      var c = cleanCity(parts[i]);
      if (!c) return null;
      out.push(c);
    }
    return out.length ? out.join(', ') : null;
  }

  function renderRecs(root, recs, isStormSafety) {
    var wrap = $(root, '[data-pf-recs]');
    var label = $(root, '[data-pf-recs-label]');
    if (!wrap) return;

    // Clear previously rendered dynamic cards.
    var old = wrap.querySelectorAll('[data-pf-card]');
    for (var i = 0; i < old.length; i++) old[i].parentNode.removeChild(old[i]);

    if (isStormSafety || recs.all.length === 0) {
      // Safety-first (storm) or no safe/available product → no product cards.
      show(wrap, false);
      show(label, false);
      return;
    }

    show(label, true);
    show(wrap, true);
    for (var j = 0; j < recs.all.length; j++) {
      wrap.appendChild(buildCard(recs.all[j], j === 0));
    }
  }

  function buildCard(p, featured) {
    var a = document.createElement('a');
    a.className = 'pf__card' + (featured ? ' pf__card--featured' : '');
    a.setAttribute('href', p.url);
    a.setAttribute('data-pf-card', '1');
    var badge = featured ? '<span class="pf__badge">Featured</span>' : '';
    var img = p.image ? '<span class="pf__card-media"><img src="' + p.image + '" alt="" loading="lazy" width="72" height="72"></span>' : '';
    a.innerHTML =
      img +
      '<span class="pf__card-body">' +
        badge +
        '<span class="pf__card-title"></span>' +
        '<span class="pf__card-price"></span>' +
        '<span class="pf__card-reason"></span>' +
      '</span>';
    a.querySelector('.pf__card-title').textContent = p.title;
    a.querySelector('.pf__card-price').textContent = p.price || '';
    a.querySelector('.pf__card-reason').textContent = p.reason || '';
    return a;
  }

  function renderFallback(root) { setState(root, 'fallback'); }
  function setState(root, state) {
    root.classList.remove('pf--no-js', 'pf--loading', 'pf--ready', 'pf--fallback');
    root.classList.add('pf--' + state);
  }

  /* -------------------------------- init ----------------------------------- */
  function initRoot(root) {
    if (!root || root.getAttribute('data-pf-init') === '1') return;
    root.setAttribute('data-pf-init', '1');
    var cfg = readConfig(root);

    // Test mode: use a local mock, never a network call.
    if (cfg.testMode && cfg.testMode !== 'off' && MOCKS[cfg.testMode]) {
      setState(root, 'loading');
      var m = MOCKS[cfg.testMode];
      var mockData = {
        location: cfg.location, tz: 'America/Toronto', updated: '2026-07-24 12:15',
        current: { tempC: m.tempC, code: m.code, condition: m.condition, windKph: m.windKph, gustKph: m.gustKph, uv: m.uv, isDay: m.isDay },
        today: { maxTempC: m.maxTempC, minTempC: m.minTempC, maxWindKph: m.maxWindKph, precipMm: m.precipMm, chanceOfRain: m.chanceOfRain, uv: m.uv },
        alerts: m.alert ? [{ event: 'Severe weather (test)', severity: 'moderate' }] : [],
        stale: false, source: 'mock'
      };
      setTimeout(function () { try { render(root, cfg, mockData); } catch (e) { renderFallback(root); } }, 250);
      return;
    }

    /* Anti-flicker: if this browser already knows the visitor's city, write the
       label before any network call, so a returning visitor never sees Fonthill
       swap to their own town. A first-time visitor keeps the server-rendered
       Niagara label until the reading arrives, which is one quiet text update
       inside an already-loading card rather than a jump between two cities. */
    if (!cfg.endpoint) {
      try {
        var primed = readSavedLocation();
        if (primed && primed.label) setText($(root, '[data-pf-location]'), primed.label);
      } catch (e) { /* priming is cosmetic; never block the render */ }
    }

    // Keyless mode needs no endpoint; proxy mode uses cfg.endpoint. Either way
    // getData() resolves to normalised weather (or null → polished fallback).
    setState(root, 'loading');
    getData(cfg).then(function (data) {
      if (data) { try { render(root, cfg, data); } catch (e) { renderFallback(root); } }
      else { renderFallback(root); }
    });
  }

  function scan() { var r = document.querySelectorAll('.poolside-forecast'); for (var i = 0; i < r.length; i++) initRoot(r[i]); }

  window.PoolForecast = {
    __loaded: true, scan: scan, initRoot: initRoot,
    activeStates: activeStates, buildRecommendations: buildRecommendations,
    conditionLabel: conditionLabel, normalizeOpenMeteo: normalizeOpenMeteo,
    // Location pipeline, exposed for _preview/pf-location-test.mjs.
    validateLocation: validateLocation, resolveLocation: resolveLocation,
    haversineKm: haversineKm, nearestPlace: nearestPlace, cleanCity: cleanCity,
    displayLabel: displayLabel, STORE: STORE, SERVICE_RADIUS_KM: SERVICE_RADIUS_KM
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan); else scan();
  document.addEventListener('shopify:section:load', function (e) {
    var c = e.target; if (!c) { scan(); return; }
    var r = c.querySelectorAll ? c.querySelectorAll('.poolside-forecast') : [];
    for (var i = 0; i < r.length; i++) initRoot(r[i]);
    if (c.classList && c.classList.contains('poolside-forecast')) initRoot(c);
  });
})();